import React, { useState, useEffect } from 'react';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import {
    Box, Text, Button, Link, Divider, Icon
} from 'components/ui';
import store from 'Store';
import Actions from 'Actions';
import ErrorList from 'components/ErrorList';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import externalAuthService from 'services/api/authentication/externalAuth';
import UrlUtils from 'utils/Url';
import userUtils from 'utils/User';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';
import {
    colors, space, lineHeights, fontSizes, fontWeights, mediaQueries
} from 'style/config';
import AuthGrantEN from './locales/AuthGrant_en_US';
import AuthGrantFR from './locales/AuthGrant_fr_CA';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(AuthGrantEN, AuthGrantFR);

/**
 * External Auth Authorization Grant Page
 * OAuth2.0 authorization page where user approves/denies third-party application access
 *
 * Flow:
 * 1. User arrives here after successful external auth login
 * 2. Retrieve session_id from localStorage or URL parameters
 * 3. Parse requested scopes from external auth params
 * 4. Display permissions list and third-party application information
 * 5. User reviews and clicks "Authorize" or "Deny"
 * 6. Submit decision via POST to /oauth2/grant API endpoint
 * 7. Redirect user to third-party app using redirectUrl from API response
 */
const AuthGrant = () => {
    const [errorMessages, setErrorMessages] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [isSignedIn, setIsSignedIn] = useState(null);

    useEffect(() => {
        // Get session_id from URL params or localStorage
        store.dispatch(Actions.showInterstice(true));
        const getSessionId = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const sessionIdFromUrl = urlParams.get('session_id');
            const sessionIdFromStorage = Storage.local.getItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID);

            return sessionIdFromUrl || sessionIdFromStorage;
        };

        const currentSessionId = getSessionId();

        setSessionId(currentSessionId);

        if (!currentSessionId) {
            setIsSignedIn(false);
            store.dispatch(Actions.showInterstice(false));
            setErrorMessages([
                {
                    errorMessage: getText('sessionIdNotFound')
                }
            ]);
            // TODO: Implement proper OAuth2 error redirect to third-party app

            return;
        }

        // Wait for user status to be resolved before showing UI
        userUtils.validateUserStatus().then(() => {
            setIsSignedIn(userUtils.isSignedIn());
            store.dispatch(Actions.showInterstice(false));

            // Fire success tracking only when navigated from Login page (not on reload)
            const previousPageData = anaUtils.getPreviousPageData(true);

            if (previousPageData?.chatgptSignInSuccess) {
                processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                    data: {
                        eventStrings: [anaConsts.Event.SIGN_IN_ATTEMPT, anaConsts.Event.SIGN_IN_SUCCESS, anaConsts.Event.EVENT_313],
                        actionInfo: anaConsts.LinkData.CHATGPT_SUCCESSFUL_SIGN_IN,
                        pageName: anaConsts.CHATGPT_SIGN_IN_PAGE_NAME,
                        pageType: anaConsts.PAGE_TYPES.SIGN_IN,
                        pageDetail: anaConsts.PAGE_DETAIL.SIGN_IN
                    }
                });
            }
        });
    }, []);

    /**
     * Handle user's authorization decision (approve/deny)
     * Submits the decision to backend and redirects user to third-party app
     * @param {boolean} approved - Whether user approved or denied the authorization request
     */
    const handleGrant = async approved => {
        store.dispatch(Actions.showInterstice(true));
        setErrorMessages([]);

        try {
            const response = await externalAuthService.submitGrant(sessionId, approved);

            // Expected response contains redirectUrl to third-party app
            // Example: { redirectUrl: "https://chatgpt.com/connector_platform_oauth_redirect?code=..." }
            if (response.redirectUrl) {
                // Clean up external auth session data from localStorage
                Storage.local.removeItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID);
                Storage.local.removeItem(LOCAL_STORAGE.EXTERNAL_AUTH_PARAMS);
                store.dispatch(Actions.showInterstice(false));
                // Redirect user back to third-party application with OAuth2 authorization code
                UrlUtils.redirectTo(response.redirectUrl);
            } else {
                throw { errorMessage: getText('authorizationFailed') };
            }
        } catch (error) {
            // Handle 403 responses and error structure: {error: "invalid_session", errorDescription: "Session ID mismatch"}
            // Extract error message from various possible fields
            const errorMessage =
                error?.errorDescription || error?.error_description || error?.errorMessage || error?.message || getText('authorizationFailed');

            setErrorMessages([{ errorMessage }]);
            store.dispatch(Actions.showInterstice(false));
        }
    };

    const handleApprove = () => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                actionInfo: anaConsts.LinkData.CHATGPT_AUTHORIZATION_ALLOW,
                pageName: anaConsts.CHATGPT_AUTHORIZATION_PAGE_NAME,
                pageType: anaConsts.CHATGPT,
                pageDetail: anaConsts.PAGE_NAMES.SEPHORA_AUTHORIZATION
            }
        });
        // Delay to allow the S.TL beacon to complete before the redirect
        setTimeout(() => handleGrant(true), 500);
    };

    const handleCancel = () => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                actionInfo: anaConsts.LinkData.CHATGPT_AUTHORIZATION_CANCEL,
                pageName: anaConsts.CHATGPT_AUTHORIZATION_PAGE_NAME,
                pageType: anaConsts.CHATGPT,
                pageDetail: anaConsts.PAGE_NAMES.SEPHORA_AUTHORIZATION
            }
        });
        // Delay to allow the S.TL beacon to complete before the redirect
        setTimeout(() => handleGrant(false), 500);
    };

    /**
     * Render the requested permissions based on OAuth2 scopes
     * Maps scope strings to user-friendly permission descriptions
     */
    const renderPermissions = () => {
        const prefix = isSignedIn ? 'permission' : 'permissionGuest';
        const permissions = [`${prefix}1`, `${prefix}2`, `${prefix}3`];

        if (isSignedIn) {
            permissions.push(`${prefix}4`);
        }

        return (
            <Box>
                {permissions.map(key => (
                    <Box
                        key={key}
                        display='flex'
                        alignItems='flex-start'
                        css={styles.permissionItem}
                    >
                        <Icon
                            name='checkmark'
                            size={fontSizes.base}
                            color={colors.gray}
                            css={styles.checkIcon}
                        />
                        <Text
                            fontSize={fontSizes.sm}
                            lineHeight={lineHeights.tight}
                            color={colors.black}
                        >
                            {getText(key)}
                        </Text>
                    </Box>
                ))}
            </Box>
        );
    };

    if (isSignedIn === null) {
        return null;
    }

    return (
        <div>
            {/* Header with Logo and Divider */}
            <Box css={styles.header}>
                <Box css={styles.logoContainer}>
                    <img
                        src='/img/ufe/logo.svg'
                        alt='Sephora'
                        style={styles.logo}
                    />
                </Box>
                <Divider color='midGray' />
            </Box>

            {/* Page Content */}
            <Box
                minHeight='100vh'
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='flex-start'
                css={styles.pageContainer}
            >
                {/* Content Container */}
                <Box css={styles.contentContainer}>
                    {/* Error Messages */}
                    {errorMessages.length > 0 && (
                        <Box css={styles.errorContainer}>
                            <ErrorList errors={errorMessages} />
                        </Box>
                    )}

                    {/* Page Title */}
                    <Box css={{ marginBottom: 24 }}>
                        <Text
                            is='h1'
                            fontSize={fontSizes.lg}
                            fontWeight={fontWeights.bold}
                            textAlign='left'
                            lineHeight={lineHeights.tight}
                        >
                            {getText(isSignedIn ? 'pageTitle' : 'pageTitleGuest')}
                        </Text>
                    </Box>

                    {/* What Can Do Section */}
                    <Box css={{ marginBottom: 24 }}>
                        <Text
                            fontSize={fontSizes.md}
                            fontWeight={fontWeights.bold}
                            css={{ marginBottom: space[2] }}
                            lineHeight={lineHeights.base}
                        >
                            {getText('whatCanDo')}
                        </Text>
                        {renderPermissions()}
                    </Box>

                    {/* Personalized Experience Section - Guest only */}
                    {!isSignedIn && (
                        <Box css={{ marginTop: space[4], marginBottom: space[4] }}>
                            <Text
                                fontSize={fontSizes.md}
                                fontWeight={fontWeights.bold}
                                css={{ marginBottom: space[2] }}
                                lineHeight={lineHeights.base}
                            >
                                {getText('personalizedHeader')}
                            </Text>
                            <Text
                                is='p'
                                fontSize={fontSizes.sm}
                                lineHeight={lineHeights.tight}
                                color={colors.black}
                            >
                                {getText('personalizedText')}
                            </Text>
                        </Box>
                    )}

                    {/* Allow Access / Continue Button */}
                    <Box css={{ marginBottom: space[3] }}>
                        <Button
                            type='button'
                            width='100%'
                            variant='primary'
                            onClick={handleApprove}
                            paddingY={space[3]}
                        >
                            {getText(isSignedIn ? 'allowAccessButton' : 'continueButton')}
                        </Button>
                    </Box>

                    {/* Cancel Button */}
                    <Box css={{ marginBottom: 24 }}>
                        <Button
                            type='button'
                            width='100%'
                            variant='secondary'
                            onClick={handleCancel}
                            paddingY={space[3]}
                        >
                            {getText('cancelButton')}
                        </Button>
                    </Box>

                    {/* Divider below Cancel button */}
                    <Divider
                        color='midGray'
                        css={{ marginBottom: 24 }}
                    />

                    {/* Manage Access Text */}
                    <Box css={{ marginBottom: space[3] }}>
                        <Text
                            fontSize={fontSizes.sm}
                            color={colors.black}
                            lineHeight={lineHeights.tight}
                            textAlign='left'
                        >
                            {getText('manageAccessText')}
                        </Text>
                    </Box>

                    {/* Privacy Policy Links */}
                    <Box>
                        <Text
                            fontSize={fontSizes.xs}
                            color={colors.gray}
                            lineHeight={lineHeights.tight}
                            textAlign='left'
                        >
                            {getText('privacyPrefix')}{' '}
                            <Link
                                href='https://openai.com/policies/privacy-policy'
                                target='_blank'
                                color={colors.blue}
                                fontSize={fontSizes.xs}
                                css={styles.legalLink}
                            >
                                {getText('chatGPTPrivacyPolicy')}
                            </Link>
                            {getText('privacyMiddle')}{' '}
                            <Link
                                href='/privacy-policy'
                                target='_blank'
                                color={colors.blue}
                                fontSize={fontSizes.xs}
                                css={styles.legalLink}
                            >
                                {getText('sephoraPrivacyPolicy')}
                            </Link>
                            {getText('privacyEnd')}
                        </Text>
                    </Box>
                </Box>
                {/* End Content Container */}
            </Box>
        </div>
    );
};

const styles = {
    header: {
        paddingTop: 27,
        paddingBottom: 0,
        backgroundColor: colors.white,
        textAlign: 'center'
    },
    logoContainer: {
        paddingBottom: 26
    },
    logo: {
        height: space[5],
        width: 'auto'
    },
    pageContainer: {
        paddingTop: 60,
        paddingBottom: 60,
        paddingLeft: space[4],
        paddingRight: space[4],
        backgroundColor: colors.white,
        position: 'relative'
    },
    contentContainer: {
        maxWidth: 400,
        width: '100%',
        [mediaQueries.xsMax]: {
            maxWidth: '100%'
        }
    },
    errorContainer: {
        marginBottom: space[5]
    },
    permissionItem: {
        marginTop: space[2]
    },
    checkIcon: {
        marginRight: space[2],
        marginTop: 2,
        flexShrink: 0
    },
    legalLink: {
        textDecoration: 'underline',
        display: 'inline',
        verticalAlign: 'baseline'
    }
};

export default wrapFunctionalComponent(AuthGrant, 'AuthGrant');
